import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Copy, CalendarSync } from "lucide-react";

export default function AccountDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);

  const feedUrl = user?.feed_token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/calendar/feed/${user.feed_token}.ics`
    : "";

  const save = async () => {
    if (!currentPw) { toast.error("Enter your current password"); return; }
    if (!newEmail && !newPw) { toast.error("Nothing to change"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-credentials", {
        current_password: currentPw,
        new_email: newEmail || undefined,
        new_password: newPw || undefined,
      });
      toast.success("Credentials updated");
      setCurrentPw(""); setNewEmail(""); setNewPw("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  const copyFeed = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      toast.success("Feed URL copied");
    } catch {
      toast.error("Copy failed — long-press and copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#866D4B]/40 w-[calc(100vw-1.5rem)] sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[var(--sn-gold)]">My Account</DialogTitle>
          <DialogDescription className="text-neutral-400">{user?.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-xs uppercase tracking-widest text-[#866D4B] font-semibold">Change email or password</div>
          <div className="space-y-2">
            <Label>Current password *</Label>
            <Input data-testid="account-current-password" type="password" value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)} placeholder="Required to make changes"
              className="bg-neutral-900 border-[#866D4B]/40" />
          </div>
          <div className="space-y-2">
            <Label>New email (optional)</Label>
            <Input data-testid="account-new-email" type="email" value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)} placeholder={user?.email}
              className="bg-neutral-900 border-[#866D4B]/40" />
          </div>
          <div className="space-y-2">
            <Label>New password (optional, min 6)</Label>
            <Input data-testid="account-new-password" type="password" minLength={6} value={newPw}
              onChange={(e) => setNewPw(e.target.value)} placeholder="••••••••"
              className="bg-neutral-900 border-[#866D4B]/40" />
          </div>
          <Button data-testid="account-save-button" onClick={save} disabled={saving}
            className="w-full bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>

        <div className="space-y-3 pt-4 border-t border-[#866D4B]/30">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#866D4B] font-semibold">
            <CalendarSync className="w-4 h-4" /> Calendar Sync (iCal)
          </div>
          <p className="text-xs text-neutral-400">
            Subscribe with your personal URL — you&apos;ll see all chapter events plus anything targeted at you.
          </p>
          <div className="flex gap-2">
            <Input data-testid="feed-url-input" readOnly value={feedUrl}
              className="bg-neutral-900 border-[#866D4B]/40 font-mono text-xs" />
            <Button data-testid="copy-feed-button" onClick={copyFeed} variant="outline"
              className="border-[var(--sn-gold)] text-[var(--sn-gold)] hover:bg-white/5 shrink-0">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <div className="text-[11px] text-neutral-500 space-y-1">
            <p><span className="text-neutral-300 font-semibold">Apple:</span> Calendar app → File → New Calendar Subscription → paste URL. On iPhone: Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar.</p>
            <p><span className="text-neutral-300 font-semibold">Google:</span> calendar.google.com → Other calendars (+) → From URL → paste URL.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
